/* ----------------------------------------------------------------------------------------------------------------------
 * C PRODUCT HOTSPOTS
 * ----------------------------------------------------------------------------------------------------------------------
 * <c-product-hotspots> — interactive callout dots overlaid on a single product gallery image.
 * See snippets/c-product-hotspots.liquid and docs/pdp-image-hotspots-plan.md for the full spec.
 *
 * Responsibilities (plan §7):
 * - Only the settled/active slide's overlay is interactive. Every other instance gets [hidden] + [inert] so its
 *   dots never take focus (plan §7.2). State is tracked via the parent <media-carousel>'s carousel:change /
 *   carousel:settle events (event.detail.slide).
 * - The dot is a real <button>, already exempt from MediaCarousel._onGalleryClick's "advance the slide" handling
 *   (assets/theme.js). The tooltip card/dock is not a button, so any click landing on it must not bubble up to
 *   <media-carousel> — this component stops propagation on every click within itself (plan §7.1).
 * - Disclosure pattern, not role="tooltip": aria-expanded toggles, one card open at a time, Esc closes, and
 *   the underlying layout switches between a per-dot anchored card (tablet+) and a single docked bottom panel
 *   (mobile, plan §6.3) without any change to the keyboard/interaction model.
 * ---------------------------------------------------------------------------------------------------------------------- */
(() => {
  if (window.customElements.get("c-product-hotspots")) return;

  // Mirrors the CSS "tablet" breakpoint (AGENTS.md mandatory breakpoints use `@media (width >= 768px)`; the
  // classic `(min-width: 768px)` syntax is used here for matchMedia() only, for the widest possible JS support).
  const DOCK_BREAKPOINT_QUERY = "(min-width: 768px)";
  const HOVER_CAPABLE_QUERY = "(hover: hover) and (pointer: fine)";

  // Grace period before a hover-opened card closes on mouseleave. The card sits ~16px clear of the dot at the
  // reference image size (offset-x 34px vs. an 18px active-dot radius), so travelling from the dot to the card
  // crosses a gap that would otherwise read as "left the hotspot" and close it mid-reach.
  const HOVER_CLOSE_DELAY_MS = 120;

  // Referenced from both close() and _positionCard(), which have to stay in sync — a card left holding a flip
  // class after closing would open on the wrong side next time.
  const FLIP_X_CLASS = "c-product-hotspots__card--flip-x";
  const FLIP_Y_CLASS = "c-product-hotspots__card--flip-y";

  class ProductHotspots extends HTMLElement {
    connectedCallback() {
      this._slide = this.closest(".product-gallery__media");
      this._carousel = this.closest("media-carousel");
      this._dock = this.querySelector(".c-product-hotspots__dock");
      // Children are read once, here — which relies on this element being upgraded *after* its subtree has
      // been parsed. That is what the `defer` on the script tag in sections/c-products.liquid buys: the class
      // is not defined until the document is complete, so connectedCallback always sees the full subtree.
      // Drop `defer` (or inline the script above the markup) and the parser upgrades this element the moment
      // it sees the opening tag — _items comes back empty and every dot silently stops responding.
      this._items = Array.from(this.querySelectorAll(".c-product-hotspots__item"));
      this._openItem = null;
      this._hoverCloseTimer = null;

      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
      this._onCarouselMove = this._onCarouselMove.bind(this);

      this.addEventListener("click", this._onClick);
      this.addEventListener("keydown", this._onKeydown);
      document.addEventListener("click", this._onDocumentClick);
      document.addEventListener("keydown", this._onDocumentKeydown);

      this._dock?.querySelector(".c-product-hotspots__dock-close")?.addEventListener("click", () => this.closeAll());

      if (this._carousel) {
        this._carousel.addEventListener("carousel:change", this._onCarouselMove);
        this._carousel.addEventListener("carousel:settle", this._onCarouselMove);
      }

      this._initActiveState();

      // Cached once: on a hover-capable device, mouseenter already opens the card, so a click on the same
      // (already open) dot must NOT toggle it closed again — that would immediately undo the hover. Toggle-to-close
      // on a repeat click is reserved for devices with no hover affordance (touch, plan §6.3: "tap the same dot
      // again to close").
      this._hoverCapable = window.matchMedia(HOVER_CAPABLE_QUERY).matches;

      // Bound on the item rather than the dot so that moving the pointer from the dot onto its own card counts
      // as staying within the hotspot — leaving the item (dot + card together) is what closes it, so no hotspot
      // is ever left in the active state without the pointer on it.
      if (this._hoverCapable) {
        this._items.forEach((item) => {
          item.addEventListener("mouseenter", () => {
            this._cancelHoverClose();
            this.open(item);
          });
          item.addEventListener("mouseleave", () => this._scheduleHoverClose(item));
        });
      }
    }

    _cancelHoverClose() {
      if (this._hoverCloseTimer === null) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
    }

    _scheduleHoverClose(item) {
      this._cancelHoverClose();
      this._hoverCloseTimer = setTimeout(() => {
        this._hoverCloseTimer = null;
        // Re-check on fire: another item may have taken over during the grace period, in which case closing
        // here would wrongly shut the newly opened one.
        if (this._openItem === item) this.closeAll();
      }, HOVER_CLOSE_DELAY_MS);
    }

    disconnectedCallback() {
      this._cancelHoverClose();
      document.removeEventListener("click", this._onDocumentClick);
      document.removeEventListener("keydown", this._onDocumentKeydown);
      this._carousel?.removeEventListener("carousel:change", this._onCarouselMove);
      this._carousel?.removeEventListener("carousel:settle", this._onCarouselMove);
    }

    /**
     * Determine the initial active/inactive state without depending on <media-carousel>'s own JS having
     * already run (custom element upgrade order between a parent and child is not guaranteed) — read the
     * carousel's initial-index attribute and this slide's DOM position directly instead.
     */
    _initActiveState() {
      if (!this._carousel || !this._slide) {
        this._setActive(true);
        return;
      }

      const initialIndex = parseInt(this._carousel.getAttribute("initial-index") || "0", 10);
      const index = Array.from(this._carousel.children).indexOf(this._slide);
      this._setActive(index === initialIndex);
    }

    _onCarouselMove(event) {
      this._setActive(event.detail.slide === this._slide);
    }

    _setActive(isActive) {
      this.hidden = !isActive;
      this.inert = !isActive;
      if (!isActive) this.closeAll();
    }

    _onClick(event) {
      // The card/dock is not a <button>, so left unchecked this click would bubble to <media-carousel> and
      // advance the slide (assets/theme.js MediaCarousel._onGalleryClick). Stop it unconditionally.
      event.stopPropagation();

      const dot = event.target.closest(".c-product-hotspots__dot");
      if (!dot) return;

      const item = dot.closest(".c-product-hotspots__item");
      if (this._openItem === item) {
        // Already open. On a hover-capable device it was very likely just opened by the mouseenter that preceded
        // this click — leave it open (closing is via Esc / a different dot / clicking outside). On touch, a repeat
        // tap on the same dot is the documented close gesture (plan §6.3).
        if (!this._hoverCapable) this.closeAll();
      } else {
        this.open(item);
      }
    }

    _onKeydown(event) {
      if (event.key !== "Escape" || !this._openItem) return;
      event.stopPropagation();
      const item = this._openItem;
      this.closeAll();
      item.querySelector(".c-product-hotspots__dot")?.focus();
    }

    _onDocumentKeydown(event) {
      if (event.key === "Escape" && this._openItem) this.closeAll();
    }

    _onDocumentClick(event) {
      if (this._openItem && !this.contains(event.target)) this.closeAll();
    }

    _isDockedLayout() {
      return Boolean(this._dock) && !window.matchMedia(DOCK_BREAKPOINT_QUERY).matches;
    }

    open(item) {
      if (!item || this.inert || this._items.indexOf(item) === -1) return;
      if (this._openItem && this._openItem !== item) this.close(this._openItem);

      const dot = item.querySelector(".c-product-hotspots__dot");
      if (!dot) return;

      this._openItem = item;
      item.classList.add("is-open");
      // Host-level flag so the affordance hint can fade out while any card is open (CSS .has-open).
      this.classList.add("has-open");
      dot.setAttribute("aria-expanded", "true");

      if (this._isDockedLayout()) {
        this._openDock(item);
        return;
      }

      const card = item.querySelector(".c-product-hotspots__card");
      if (card) {
        card.hidden = false;
        this._positionCard(item, card);
      }
    }

    close(item) {
      if (!item) return;

      item.classList.remove("is-open");
      item.querySelector(".c-product-hotspots__dot")?.setAttribute("aria-expanded", "false");

      const card = item.querySelector(".c-product-hotspots__card");
      if (card) {
        card.hidden = true;
        card.classList.remove(FLIP_X_CLASS, FLIP_Y_CLASS);
      }

      if (this._openItem === item) this._openItem = null;
      if (!this._openItem) this.classList.remove("has-open");
    }

    closeAll() {
      this._cancelHoverClose();
      if (this._openItem) this.close(this._openItem);
      this.classList.remove("has-open");
      if (this._dock) this._dock.hidden = true;
    }

    _openDock(item) {
      if (!this._dock) return;

      const title = item.querySelector(".c-product-hotspots__card-title")?.textContent ?? "";
      const body = item.querySelector(".c-product-hotspots__card-body")?.textContent ?? "";

      const dockTitle = this._dock.querySelector(".c-product-hotspots__dock-title");
      const dockBody = this._dock.querySelector(".c-product-hotspots__dock-body");
      if (dockTitle) dockTitle.textContent = title;
      if (dockBody) dockBody.textContent = body;

      this._dock.hidden = false;
    }

    /**
     * Place the card against the overlay's own bounds — plan §3.3: two Figma frames ("Ultra Durable",
     * "Made in Utah") were hand-nudged to stay inside the image, which we implement as a measurement at open
     * time rather than as per-hotspot hard-coded offsets.
     *
     * Two axes, deliberately governed differently:
     *
     * - Horizontal is *editorial*. Each hotspot's `tooltip_position` metaobject field (auto/left/right) picks
     *   the side, because which side reads better depends on what the popup would cover in the photo — a
     *   judgement call for whoever writes the content, not something geometry can infer.
     * - Vertical is *structural*. It exists only to stop the card escaping the top of the image, so it is
     *   always measured and never configurable. It still runs when a side has been forced: pinning a hotspot
     *   to the left says nothing about whether it also sits too close to the top edge.
     *
     * A forced side is a preference, not a mandate — same contract as any tooltip library's placement + flip.
     * If the requested side would spill outside the image the card mirrors to the other one, so no value
     * reachable from the admin dropdown can produce a card hanging off the photo. The mirror is only kept if
     * it actually fits; swapping one overflow for another would be no improvement.
     */
    _positionCard(item, card) {
      card.classList.remove(FLIP_X_CLASS, FLIP_Y_CLASS);

      const overlayRect = this.getBoundingClientRect();

      // Anything unrecognised (including the retired "top"/"bottom" values, should an old record still hold
      // one) degrades to "auto" rather than throwing the layout away.
      const prefersLeft = (item.dataset.tooltipPosition || "").trim().toLowerCase() === "left";
      if (prefersLeft) card.classList.add(FLIP_X_CLASS);

      const preferredOverflow = this._inlineOverflow(card, overlayRect, prefersLeft);
      if (preferredOverflow > 0) {
        card.classList.toggle(FLIP_X_CLASS);
        // Keep the mirrored side only if it is genuinely better. Neither side fits when the image is narrower
        // than a card plus its offset (the card has a 180px floor, so anything under ~400px qualifies) — there
        // the choice is between two overflows, and the smaller one wins rather than always reverting.
        if (this._inlineOverflow(card, overlayRect, !prefersLeft) >= preferredOverflow) {
          card.classList.toggle(FLIP_X_CLASS);
        }
      }

      if (card.getBoundingClientRect().top < overlayRect.top) {
        card.classList.add(FLIP_Y_CLASS);
      }
    }

    /**
     * How far past the image edge the card currently sits, in px (0 when it fits). Measured fresh on each
     * call: the caller toggles the flip class between calls, which moves the card.
     */
    _inlineOverflow(card, overlayRect, isOnLeft) {
      const cardRect = card.getBoundingClientRect();
      return Math.max(0, isOnLeft ? overlayRect.left - cardRect.left : cardRect.right - overlayRect.right);
    }
  }

  window.customElements.define("c-product-hotspots", ProductHotspots);
})();
